import { useState, useCallback, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CaretLeft, CaretRight, ArrowDown } from "@phosphor-icons/react";
import { REALISATION_PHOTOS, LEGACY_REALISATION_PHOTOS, REALISATION_CATEGORIES } from "../data/site";
import PageHero from "../components/PageHero";
import CtaBanner from "../components/CtaBanner";

const INITIAL_COUNT = 15;
const ALL_PHOTOS = [...LEGACY_REALISATION_PHOTOS, ...REALISATION_PHOTOS];

export default function Realisations() {
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState("all");
  const [showAll, setShowAll] = useState(false);

  const filtered = useMemo(
    () => (filter === "all" ? ALL_PHOTOS : ALL_PHOTOS.filter((r) => r.category === filter)),
    [filter]
  );
  const items = useMemo(
    () => (showAll ? filtered : filtered.slice(0, INITIAL_COUNT)),
    [filtered, showAll]
  );

  const changeFilter = (value) => {
    setFilter(value);
    setShowAll(false);
    setActive(null);
  };

  const close = useCallback(() => setActive(null), []);
  const next = useCallback(() => setActive((i) => (i + 1) % items.length), [items.length]);
  const prev = useCallback(() => setActive((i) => (i - 1 + items.length) % items.length), [items.length]);

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, close, next, prev]);

  const filters = [{ value: "all", label: "Tout" }, ...REALISATION_CATEGORIES];
  const remaining = filtered.length - items.length;

  return (
    <div data-testid="realisations-page">
      <PageHero
        index="02"
        eyebrow="Réalisations"
        title="Nos chantiers, en images."
        intro="Un aperçu de nos installations de chauffage, énergies renouvelables et sanitaire dans le Saintois et autour de Nancy."
      />

      <div className="mx-auto max-w-7xl px-6 pb-24 md:px-12">
        {/* Filtres par catégorie */}
        <div className="mb-10 flex flex-wrap gap-2.5" data-testid="gallery-filters">
          {filters.map((f) => (
            <button
              key={f.value}
              data-testid={`filter-${f.value}`}
              onClick={() => changeFilter(f.value)}
              className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all duration-300 ${
                filter === f.value
                  ? "border-transparent bg-[#14183A] text-white"
                  : "border-[#E5E7F0] bg-white text-[#5b6072] hover:border-[#1A49F7] hover:text-[#1A49F7]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {items.length === 0 ? (
          <p className="py-16 text-center text-[#9aa0b4]">Aucune réalisation dans cette catégorie pour le moment.</p>
        ) : (
          <div className="columns-1 gap-4 sm:columns-2 lg:columns-3 [&>*]:mb-4">
            {items.map((r, i) => (
              <motion.button
                key={`${r.src}-${i}`}
                data-testid={`gallery-item-${i}`}
                onClick={() => setActive(i)}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.06, ease: [0.22, 1, 0.36, 1] }}
                className="group relative block w-full break-inside-avoid overflow-hidden rounded-[1.5rem] bg-[#F3F6FF] text-left"
              >
                <img
                  src={r.src}
                  alt={r.caption}
                  loading="lazy"
                  className="w-full object-cover grayscale-[35%] transition-all duration-700 group-hover:scale-[1.04] group-hover:grayscale-0"
                />
                <div className="absolute inset-x-0 bottom-0 translate-y-2 bg-gradient-to-t from-black/70 to-transparent p-5 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                  <p className="text-sm font-semibold text-white">{r.caption}</p>
                </div>
              </motion.button>
            ))}
          </div>
        )}

        {remaining > 0 && (
          <div className="mt-12 flex justify-center">
            <button
              data-testid="see-all-button"
              onClick={() => setShowAll(true)}
              className="group inline-flex items-center gap-2 rounded-full bg-[#14183A] px-8 py-4 font-semibold text-white transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_12px_40px_-10px_rgba(26,73,247,0.55)]"
            >
              Voir tout ({filtered.length})
              <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
            </button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {active !== null && items[active] && (
          <motion.div
            data-testid="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-white/95 backdrop-blur-md"
            onClick={close}
          >
            <button data-testid="lightbox-close" onClick={close} className="absolute right-5 top-5 inline-flex h-12 w-12 items-center justify-center rounded-full border border-[#EEE] bg-white transition-colors hover:bg-[#14183A] hover:text-white">
              <X className="h-5 w-5" />
            </button>
            <button data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-4 inline-flex h-12 w-12 items-center justify-center rounded-full border border-[#EEE] bg-white transition-colors hover:bg-[#14183A] hover:text-white md:left-8">
              <CaretLeft className="h-5 w-5" />
            </button>
            <button data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-4 inline-flex h-12 w-12 items-center justify-center rounded-full border border-[#EEE] bg-white transition-colors hover:bg-[#14183A] hover:text-white md:right-8">
              <CaretRight className="h-5 w-5" />
            </button>
            <motion.div
              key={active}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="mx-6 max-h-[80vh] max-w-4xl overflow-hidden rounded-[1.5rem]"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={items[active].src} alt={items[active].caption} className="max-h-[72vh] w-auto object-contain" />
              <p className="bg-[#14183A] px-6 py-4 text-center text-sm font-medium text-white">{items[active].caption}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <CtaBanner />
    </div>
  );
}
